import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import { useFloating, offset, flip, shift, arrow } from '@floating-ui/react-dom';
import TimeTable from './TimeTable';
import { getColorClass, PALETTES } from '../utils/colorUtils';
import useConfig from '../config';
import { hasConflictWithSlots } from '../utils/time';

// Define the Course interface/type
interface Course {
  name: string;
  slots: string[];
  credits: number;
  colorIndex: number;
  facultyPreferences?: string[]; // Added
  facultyLabAssignments?: Array<{ facultyName: string; slots: string[] }>; // Added
  // Add other properties like color if your TimeTable/getColorClass expects them directly on the course object
  // For now, assuming name, slots, credits are primary for identification and TimeTable handles color assignment
}

interface TimeTableSlotSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { 
    courseName: string; 
    selectedSlots: string[]; 
    credits: number;
  }) => void;
  otherCoursesData?: Course[];
  editingCourse?: Course;
  slotConflictPairs: string[][]; // Added prop
  palette: keyof typeof PALETTES;
  colorIndex: number;
}

const TimeTableSlotSelector: React.FC<TimeTableSlotSelectorProps> = ({
  isOpen,
  onClose,
  onSubmit,
  otherCoursesData = [],
  editingCourse,
  slotConflictPairs, // Added prop
  palette,
  colorIndex
}) => {
  const config = useConfig();
  const [courseName, setCourseName] = useState('');
  const [credits, setCredits] = useState(0);
  const [creditInputString, setCreditInputString] = useState('0');
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const [showSlotPopup, setShowSlotPopup] = useState(false);
  const [availableSlotsForCell, setAvailableSlotsForCell] = useState<string[]>([]);
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const [slotInputString, setSlotInputString] = useState('');
  const [isTimetableOverflowing, setIsTimetableOverflowing] = useState(false);
  const timetableContainerRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const {
    x,
    y,
    strategy,
    refs,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    placement: 'top',
    middleware: [
      offset(-15),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ],
  });

  // Derive all existing slot strings from otherCoursesData
  const allExistingSlots = useMemo(() => {
    const coursesToConsider = editingCourse
      ? otherCoursesData.filter(course => course.name !== editingCourse.name)
      : otherCoursesData;

    return coursesToConsider.reduce((accSlots: string[], course) => {
      // Start with the base slots of the course (custom mode)
      const combinedSlots = new Set<string>(course.slots);

      // If it's a lab course with a selected faculty, add their slots too (selection mode)
      if (
        course.name.endsWith(' Lab') &&
        course.facultyPreferences &&
        course.facultyPreferences.length > 0 &&
        course.facultyLabAssignments
      ) {
        const firstFacultyName = course.facultyPreferences[0];
        const facultyAssignment = course.facultyLabAssignments.find(
          (a) => a.facultyName === firstFacultyName
        );

        if (facultyAssignment && facultyAssignment.slots.length > 0) {
          facultyAssignment.slots.forEach((slot) => combinedSlots.add(slot));
        }
      }

      // Add the unique combined slots for this course to the accumulator
      return [...accSlots, ...Array.from(combinedSlots)];
    }, []);
  }, [otherCoursesData, editingCourse]);

  useEffect(() => {
    const container = timetableContainerRef.current;
    if (!isOpen || !container) return;

    const observer = new ResizeObserver(() => {
      const isOverflowing = container.scrollWidth > container.clientWidth;
      setIsTimetableOverflowing(isOverflowing);
    });

    observer.observe(container);
    const isOverflowing = container.scrollWidth > container.clientWidth;
    setIsTimetableOverflowing(isOverflowing);

    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  // Helper function to check if a slot has time conflicts
  const hasTimeConflict = useCallback((slot: string, existingSlots: string[]): boolean => {
    return hasConflictWithSlots(slot, existingSlots, config);
  }, [config]);

  // Helper function to check if a slot is blocked for a new selection
  const isSlotBlockedForSelection = useCallback((
    slot: string,
    currentAllExisting: string[],
    currentSelectedBySelf: string[]
  ): boolean => {
    // 1. Blocked if the slot itself is taken by another course
    if (currentAllExisting.includes(slot)) return true;
  
    // 2. Blocked if the slot has already been selected by the user
    if (currentSelectedBySelf.includes(slot)) return true;
  
    // 3. Blocked if it has time conflicts with existing slots
    if (hasTimeConflict(slot, [...currentAllExisting, ...currentSelectedBySelf])) return true;
  
    return false;
  }, [hasTimeConflict]);

  const coursesToDisplayInTimeTable = useMemo(() => {
    const currentSelectingCourse: Course = {
      name: courseName || 'Selecting...',
      slots: selectedSlots,
      credits: credits,
      colorIndex: colorIndex
    };
    return [...(otherCoursesData || []), currentSelectingCourse];
  }, [otherCoursesData, courseName, selectedSlots, credits, colorIndex]);
  
  const selectingCourseColorClass = useMemo(() => PALETTES[palette].colors[colorIndex], [palette, colorIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      if (editingCourse) {
        // If editing, populate the form
        setCourseName(editingCourse.name);
        setCredits(editingCourse.credits);
        setCreditInputString(editingCourse.credits.toString());
        setSelectedSlots(editingCourse.slots);
        setSlotInputString(editingCourse.slots.join('+'));
      } else {
        // Otherwise, reset for a new course
        setCourseName('');
        setCredits(0);
        setCreditInputString('0');
        setSelectedSlots([]);
        setSlotInputString('');
      }

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.timetable-container') && !target.closest('.slot-popup')) {
          setShowSlotPopup(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.body.style.overflow = 'auto';
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } else {
      // Reset state when the modal closes
      setCourseName('');
      setCredits(0);
      setCreditInputString('0');
      setSelectedSlots([]);
      setShowSlotPopup(false);
      setSlotInputString('');
    }
  }, [isOpen, editingCourse]);

  const handleCellClick = (event: React.MouseEvent<HTMLButtonElement>, cellSlots: string) => {
    if (!cellSlots) return;

    const slotsInCell = cellSlots.split('/');

    // Stricter check: If any part of the cell is taken by another course, block the entire cell interaction.
    const isCellTaken = slotsInCell.some(slot => allExistingSlots.includes(slot) && !(editingCourse?.slots.includes(slot)));
    if (isCellTaken) {
        console.warn(`Cell ${cellSlots} is blocked because it contains an already taken slot.`);
        return; // Do nothing.
    }

    // The rest of the logic can now assume the cell is not taken by another course.
    // It only needs to handle deselection or conflicts with the current selection.

    const alreadySelectedInCell = slotsInCell.filter(slot => selectedSlots.includes(slot));
    if (alreadySelectedInCell.length > 0) {
      // Deselect logic
      const newSelectedSlots = selectedSlots.filter(s => !alreadySelectedInCell.includes(s));
      setSelectedSlots(newSelectedSlots);
      setSlotInputString(newSelectedSlots.join('+'));
      setShowSlotPopup(false);
      return;
    }
    
    // Check for availability based on conflicts with the current selection
    const availableForSelection = slotsInCell.filter(slot => 
        !hasTimeConflict(slot, selectedSlots)
    );

    if (availableForSelection.length === 0) {
      console.warn(`All slots in ${cellSlots} conflict with your current selection.`);
      setShowSlotPopup(false);
      return;
    }

    if (availableForSelection.length === 1) {
      const slotToAdd = availableForSelection[0];
      const newSelectedSlots = [...selectedSlots, slotToAdd];
      setSelectedSlots(newSelectedSlots);
      setSlotInputString(newSelectedSlots.join('+'));
      setShowSlotPopup(false);
    } else {
      if (refs.setReference && event.currentTarget) {
        refs.setReference(event.currentTarget);
      }
      setSelectedCell(cellSlots);
      setAvailableSlotsForCell(availableForSelection);
      setShowSlotPopup(true);
    }
  };

  const handleSlotSelectFromPopup = (slot: string) => {
    // If the slot is already selected, allow deselection
    if (selectedSlots.includes(slot)) {
      const newSelectedSlots = selectedSlots.filter(s => s !== slot);
      setSelectedSlots(newSelectedSlots);
      setSlotInputString(newSelectedSlots.join('+'));
      setShowSlotPopup(false);
      return;
    }

    // If the slot is taken by another course, prevent selection
    if (allExistingSlots.includes(slot)) {
      console.warn(`Attempted to select slot ${slot} from popup, but it's already taken by another course.`);
      setShowSlotPopup(false);
      return;
    }

    // Check for time conflicts
    if (hasTimeConflict(slot, [...allExistingSlots, ...selectedSlots])) {
      console.warn(`Attempted to select slot ${slot} from popup, but it has time conflicts.`);
      setShowSlotPopup(false);
      return;
    }

    // If all checks pass, add the slot
    const newSelectedSlots = [...selectedSlots, slot];
    setSelectedSlots(newSelectedSlots);
    setSlotInputString(newSelectedSlots.join('+'));
    setShowSlotPopup(false);
  };

  const handleSlotInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentInput = event.target.value;
    setSlotInputString(currentInput); // Always update the input string

    const potentialSlotsRaw = currentInput.toUpperCase().split('+');
    const potentialSlots = potentialSlotsRaw.map(s => s.trim()).filter(s => s !== '');
    
    const newValidSelectedSlots: string[] = [];

    // First, keep any slots that were already selected (to allow for deselection)
    const slotsToRemove = selectedSlots.filter(slot => !potentialSlots.includes(slot));
    const slotsToKeep = selectedSlots.filter(slot => potentialSlots.includes(slot));
    newValidSelectedSlots.push(...slotsToKeep);

    // Then try to add new slots
    for (const slotToAdd of potentialSlots) {
      // Skip if slot is already in our valid selection list
      if (newValidSelectedSlots.includes(slotToAdd)) {
        continue;
      }

      // Skip if slot is taken by another course
      if (allExistingSlots.includes(slotToAdd)) {
        console.warn(`Slot ${slotToAdd} is already taken by another course.`);
        continue;
      }

      // Skip if slot would create a time conflict
      if (hasTimeConflict(slotToAdd, [...allExistingSlots, ...newValidSelectedSlots])) {
        console.warn(`Slot ${slotToAdd} has time conflicts.`);
        continue;
      }

      newValidSelectedSlots.push(slotToAdd);
    }
    
    setSelectedSlots(newValidSelectedSlots);
  };

  const handleSubmit = () => {
    if (courseName && selectedSlots.length > 0) {
      onSubmit({
        courseName,
        selectedSlots,
        credits
      });
      onClose();
    }
  };

  const getCellClickableClass = (
    cellSlotString: string,
    currentSelectedSlots: string[],
    currentExistingSlots: string[],
    isCellWithPopupOpen: boolean
  ): string => {
    const cellSlotParts = cellSlotString.split('/');
    
    // Stricter check: If any part of the cell is taken by another course, the whole cell is non-interactive.
    const isCellTakenByOtherCourse = cellSlotParts.some(part => 
      currentExistingSlots.includes(part) && !(editingCourse?.slots.includes(part))
    );

    if (isCellTakenByOtherCourse) {
      return 'opacity-50 cursor-not-allowed';
    }

    // Now, check interactivity based on the current user's selections.
    const alreadySelectedInCell = cellSlotParts.some(part => currentSelectedSlots.includes(part));
    if (alreadySelectedInCell) {
      return 'transition-colors border border-transparent hover:border-black'; // It's clickable for deselection
    }

    // Finally, check if selecting from this cell would cause a conflict with what's already selected.
    // The cell is un-clickable if all its parts conflict with the current selection.
    const isBlockedByConflictWithSelf = cellSlotParts.every(part => 
        hasTimeConflict(part, currentSelectedSlots)
    );

    if (isBlockedByConflictWithSelf) {
      return 'opacity-50 cursor-not-allowed';
    }

    let classes = 'transition-colors border border-transparent';
    if (!isCellWithPopupOpen) {
      classes += ' hover:border-black';
    }
    return classes;
  };

  const dayRows = [
    {
      day: 'MON',
      slots: ['A1/L1', 'F1/L2', 'D1/L3', 'TB1/L4', 'TG1/L5', 'L6', 'A2/L31', 'F2/L32', 'D2/L33', 'TB2/L34', 'TG2/L35', 'L36']
    },
    {
      day: 'TUE',
      slots: ['B1/L7', 'G1/L8', 'E1/L9', 'TC1/L10', 'TAA1/L11', 'L12', 'B2/L37', 'G2/L38', 'E2/L39', 'TC2/L40', 'TAA2/L41', 'L42']
    },
    {
      day: 'WED',
      slots: ['C1/L13', 'A1/L14', 'F1/L15', 'D1', 'TB1', 'L18', 'C2/L43', 'A2/L44', 'F2/L45', 'TD2/L46', 'TBB2/L47', 'L48']
    },
    {
      day: 'THU',
      slots: ['D1/L19', 'B1/L20', 'G1/L21', 'TE1/L22', 'TCC1/L23', 'L24', 'D2/L49', 'B2/L50', 'G2/L51', 'TE2/L52', 'TCC2/L53', 'L54']
    },
    {
      day: 'FRI',
      slots: ['E1/L25', 'C1/L26', 'TA1/L27', 'TF1/L28', 'TD1/L29', 'L30', 'E2/L55', 'C2/L56', 'TA2/L57', 'TF2/L58', 'TDD2/L59', 'L60']
    }
  ];

  const cellStyle = {
    width: '80px',
    height: '80px',
    minWidth: '80px',
    minHeight: '80px'
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm"></div>
      <div className="bg-white rounded-2xl p-6 w-[99.5vw] max-w-[3000px] z-10 relative transform scale-70 origin-center">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-semibold text-gray-900">{editingCourse ? 'Edit Course Slots' : 'Select Course Slots'}</h2>
            <div className="flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-800 relative top-[1px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-black text-sm relative top-[1px]">Choose slots by clicking cells (no faculty preferences)</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-red-500 transition-colors">
            <IoClose size={24} />
          </button>
        </div>

        <div className="grid grid-cols-[4.2fr,1fr] gap-8">
          {/* Left side - Interactive TimeTable */}
          <div 
            ref={timetableContainerRef}
            className={`border rounded-xl timetable-container ${isTimetableOverflowing ? 'overflow-x-auto' : 'overflow-x-hidden'}`}
          >
            <div className="min-w-[1200px]"> {/* Increased minimum width */}
              <TimeTable 
                courses={coursesToDisplayInTimeTable}
                isSelectMode={true}
                onCellClick={handleCellClick}
                selectedSlots={selectedSlots}
                existingSlots={allExistingSlots}
                getCellInteractionClass={(cellSlots: string) => getCellClickableClass(cellSlots, selectedSlots, allExistingSlots, showSlotPopup && selectedCell === cellSlots)}
                hideContentForCell={showSlotPopup && selectedCell ? selectedCell : undefined}
                palette={palette}
              />
            </div>
          </div>

          {/* Right side - Course Details */}
          <div className="border border-gray-300 rounded-xl p-6"> {/* Increased padding */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Name
                </label>
                <input
                  type="text"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:border-black transition-all"
                  placeholder="Enter course name"
                />
              </div>

              <div>
                <label htmlFor="slot-input-tts" className="block text-sm font-medium text-gray-700 mb-2">
                  Selected Slots (e.g., A1+TA1)
                </label>
                <input
                  id="slot-input-tts"
                  type="text"
                  value={slotInputString}
                  onChange={handleSlotInputChange}
                  className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:border-black transition-all"
                  placeholder="Type slots or click cells"
                />
              </div>

              <div>
                <label htmlFor="credits-input-tts" className="block text-sm font-medium text-gray-700 mb-2">
                  Credits
                </label>
                <div className="relative flex items-center">
                  <input
                    id="credits-input-tts"
                    type="text"
                    value={creditInputString}
                    maxLength={4}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow only numbers and a single decimal point
                      if (!/^[0-9]*\.?[0-9]*$/.test(val)) return;
                      setCreditInputString(val);
                      if (val === '') {
                        setCredits(0);
                      } else {
                        const num = parseFloat(val);
                        if (!isNaN(num) && num >= 0 && num <= 99) {
                          setCredits(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === '.') {
                        setCreditInputString('0');
                        setCredits(0);
                      } else {
                        const num = parseFloat(val);
                        if (!isNaN(num) && num >= 0 && num <= 99) {
                          // Format to at most 1 decimal place
                          const formattedNum = Math.round(num * 10) / 10;
                          setCreditInputString(formattedNum.toString());
                          setCredits(formattedNum);
                        } else {
                          setCreditInputString(credits.toString());
                        }
                      }
                    }}
                    className="w-full text-center text-base border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-0 focus:border-black pr-10"
                    aria-label="Credits value"
                  />
                  <div className="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-center pr-1">
                    <button
                      type="button"
                      onClick={() => {
                        const newCredits = Math.min(99, credits + 0.5);
                        setCredits(newCredits);
                        setCreditInputString(newCredits.toString());
                      }}
                      className="h-1/2 px-1 text-gray-500 hover:text-gray-700 flex items-center justify-center rounded-tr-md focus:outline-none"
                      aria-label="Increase credits"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const newCredits = Math.max(0, credits - 0.5);
                        setCredits(newCredits);
                        setCreditInputString(newCredits.toString());
                      }}
                      className="h-1/2 px-1 text-gray-500 hover:text-gray-700 flex items-center justify-center rounded-br-md focus:outline-none"
                      aria-label="Decrease credits"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!courseName || selectedSlots.length === 0}
                className={`
                  w-full px-4 py-3 rounded-lg text-base font-medium text-white transition-colors
                  ${courseName && selectedSlots.length > 0
                    ? 'bg-black hover:bg-gray-900'
                    : 'bg-gray-300 cursor-not-allowed'
                  }
                `}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>

        {/* Replace the Slot Selection Popup */}
        {showSlotPopup && selectedCell && availableSlotsForCell.length > 1 && (
          <div
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content',
            }}
            className="z-20 slot-popup"
            onClick={(e) => e.stopPropagation()} 
          >
            <div 
              className="relative bg-white rounded-lg shadow-xl border border-gray-300 p-2"
            >
              <div className="flex flex-row items-center gap-1">
                {availableSlotsForCell.map((slot, index) => (
                  <React.Fragment key={slot}>
                    <button
                      onClick={() => handleSlotSelectFromPopup(slot)}
                      className={`
                        px-3 py-1.5 text-sm font-medium rounded-md text-left whitespace-nowrap
                        border-2 border-gray-400
                        ${selectedSlots.includes(slot)
                          ? 'bg-blue-100 text-blue-800'
                          : 'hover:bg-gray-700 hover:text-white'
                        }
                      `}
                    >
                      {slot}
                    </button>
                    {index < availableSlotsForCell.length - 1 && (
                      <span className="text-gray-400">|</span>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Arrow pointing down */}
              <div
                ref={arrowRef}
                className="absolute left-1/2 -translate-x-1/2 w-0 h-0"
                style={{
                  top: '100%',
                  borderLeft: '15px solid transparent',
                  borderRight: '15px solid transparent',
                  borderTop: '15px solid #D1D5DB',
                }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 w-0 h-0"
                style={{
                  top: 'calc(100% - 1px)',
                  borderLeft: '14px solid transparent',
                  borderRight: '14px solid transparent',
                  borderTop: '14px solid white',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeTableSlotSelector; 